// IntelligenceReview.js — verdict card, Phase 3 Slice 4.
//
// Replaces the Slice-1 design that depended on React Router state.row from
// the queue. Now drives entirely off GET /api/intelligence/leak/:id/evidence
// (added in Slice 3). That endpoint closes the long-banked refresh /
// direct-URL gap AND mints signed URLs for the master-frame JPEGs the
// operator needs to make a confident re-attribution.
//
// Mount sequence:
//   1. GET /api/intelligence/me — freshness check (deactivated staff are
//      evicted before they spend time filling the form).
//   2. GET /api/intelligence/leak/:leak_id/evidence — leak row +
//      top_matches with signed_url per master-frame JPEG.
//
// Render: side-by-side evidence (leak link on the left, primary master
// frame on the right), plain-language conflict sentence, full top_matches
// list visible so the operator can pick an alternative re-attribution
// target on tied competing_creator rows, then four action buttons.
//
// Action → POST /api/intelligence/verify body matrix:
//   "Re-attribute to {best_handle}"  → VERIFIED + MANUAL_DMCA
//                                       + corrected_creator_id={target}
//   "Confirm current creator"        → VERIFIED + MANUAL_DMCA
//                                       (NO corrected_creator_id)
//   "Not a leak / false match"       → FALSE_POSITIVE + FALSE_POSITIVE
//   "Can't tell"                     → INSUFFICIENT_EVIDENCE + OUT_OF_SCOPE
//
// MANUAL_DMCA is the default for every VERIFIED verdict — not AUTO_DMCA —
// because auto-fire is unwired today (memory: AUTO_DMCA proven metadata-
// only) and the default must be safe for when it isn't.
//
// Signed-URL TTL handling: the evidence endpoint mints URLs with 300s TTL.
// If the card has been open longer than that on submit OR the <img>
// onError fires, refetch /evidence transparently to mint fresh URLs.
// Capped at 2 retries to avoid loops on a permanently missing frame.
//
// Response handling — POST /verify returns {path: 'written'|'staged', ...}.
//   path='written' (operator OR specialist with requires_approval=false):
//     "Re-attributed." (if action was reattribute)
//     "Verdict recorded." (otherwise)
//   path='staged'  (specialist with requires_approval=true, e.g. trainee):
//     "Submitted for review."
//
// Submit error handling preserves the form (no eviction on 401 mid-submit;
// 403/404/etc. show inline with Retry).

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

const API = 'https://tribaltech-backend.onrender.com';

// Must match the backend's SIGNED_URL_TTL_SEC. The card uses this to
// decide when to refetch evidence before the next action.
const SIGNED_URL_TTL_SEC          = 300;
const REFRESH_BEFORE_EXPIRY_SEC   = 30;   // safety margin
const MAX_FRAME_REFRESH_RETRIES   = 2;
const NOTES_MAX                   = 4000;
const FRESHNESS_OPTIONS           = ['live', '404', 'paywall', 'removed', 'unknown'];
const CONFIDENCE_OPTIONS          = ['high', 'medium', 'low'];

// Pretty labels for the gate's failed_gate values shown in the conflict
// sentence + TopBar.
const FAILED_GATE_LABEL = {
  username_disagrees:      'Username disagrees',
  competing_creator:       'Competing creator',
  visual_evidence_missing: 'Visual evidence missing',
};

// Action model. Each entry defines exactly what gets POSTed to /verify
// when that button is clicked. requiresTarget gates the Submit button
// until a target creator is picked from top_matches.
const ACTION_META = {
  reattribute: {
    labelPrefix:    'Re-attribute to',
    verdict:        'VERIFIED',
    routing:        'MANUAL_DMCA',
    requiresTarget: true,
    color:          '#a78bfa',
    bg:             'rgba(124,58,237,0.18)',
    border:         '#7c3aed',
  },
  confirm: {
    label:          'Confirm current creator',
    verdict:        'VERIFIED',
    routing:        'MANUAL_DMCA',
    requiresTarget: false,
    color:          '#86efac',
    bg:             'rgba(34,197,94,0.10)',
    border:         '#22c55e',
  },
  false_positive: {
    label:          'Not a leak / false match',
    verdict:        'FALSE_POSITIVE',
    routing:        'FALSE_POSITIVE',
    requiresTarget: false,
    color:          '#f87171',
    bg:             'rgba(248,113,113,0.08)',
    border:         '#ef4444',
  },
  insufficient: {
    label:          "Can't tell",
    verdict:        'INSUFFICIENT_EVIDENCE',
    routing:        'OUT_OF_SCOPE',
    requiresTarget: false,
    color:          '#94a3b8',
    bg:             'rgba(148,163,184,0.08)',
    border:         '#475569',
  },
};

function bearerHeaders() {
  const token = localStorage.getItem('tribal_staff_token');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

function evictToLogin() {
  localStorage.removeItem('tribal_staff_token');
  localStorage.removeItem('tribal_staff_user');
  window.location.href = '/intelligence/login';
}

function relativeTime(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function endpointForRole(role) {
  return role === 'intelligence_operator' ? 'attribution-queue' : 'queue';
}

export default function IntelligenceReview() {
  const { leakId } = useParams();
  const navigate   = useNavigate();

  // ── Identity (from /me — used for staged/written success copy + the
  // next-leak endpoint choice).
  const [staff,        setStaff]        = useState(null);
  const [meError,      setMeError]      = useState('');

  // ── Evidence (from /leak/:id/evidence). The single source of truth
  // for everything the card renders — leak row + top_matches + signed
  // URLs.
  const [evidence,         setEvidence]         = useState(null);
  const [evidenceFetchedAt, setEvidenceFetchedAt] = useState(0);
  const [evidenceLoading,  setEvidenceLoading]  = useState(true);
  const [evidenceError,    setEvidenceError]    = useState('');
  const [evidenceErrorKind, setEvidenceErrorKind] = useState(''); // 'forbidden'|'not_found'|'network'|'server'

  // ── Frame-refresh tracking. The signed URL might expire mid-review;
  // when the <img> onError fires, refetch evidence — but cap retries.
  const [frameRefreshing,   setFrameRefreshing]   = useState(false);
  const [frameRefreshCount, setFrameRefreshCount] = useState(0);

  // ── Action model. action='' means nothing picked; Submit disabled.
  const [action,            setAction]            = useState('');
  const [targetCreatorId,   setTargetCreatorId]   = useState('');

  // ── Optional verification_intelligence fields the operator can add.
  // Same shape as Slice-1 card (kept for continuity); altCreator removed
  // because we now have a structured target_creator_id instead.
  const [hostObservation,   setHostObservation]   = useState('');
  const [freshness,         setFreshness]         = useState('');
  const [evidenceUrlsText,  setEvidenceUrlsText]  = useState('');
  const [confidence,        setConfidence]        = useState('');
  const [notes,             setNotes]             = useState('');

  // ── Submit state.
  const [submitting,        setSubmitting]        = useState(false);
  const [submitError,       setSubmitError]       = useState('');
  const [submitErrorKind,   setSubmitErrorKind]   = useState('');
  const [submitted,         setSubmitted]         = useState(false);
  const [submittedPath,     setSubmittedPath]     = useState(''); // 'written' | 'staged'

  // ── Next-leak loader.
  const [nextLoading,       setNextLoading]       = useState(false);

  // ── /me freshness check.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/intelligence/me`, { headers: bearerHeaders() });
        if (res.status === 401) { evictToLogin(); return; }
        if (!res.ok) {
          setMeError('Could not verify your session. Refresh to retry.');
          return;
        }
        const me = await res.json();
        setStaff(me);
      } catch {
        setMeError('Network error verifying your session. Refresh to retry.');
      }
    })();
  }, []);

  // ── Evidence fetch. Idempotent: every call mints fresh signed URLs.
  // Used on mount, on TTL refresh, and on <img> onError.
  const fetchEvidence = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/intelligence/leak/${leakId}/evidence`,
        { headers: bearerHeaders() });
      if (res.status === 401) { evictToLogin(); return; }
      if (res.status === 403) {
        setEvidenceErrorKind('forbidden');
        setEvidenceError('You are not assigned to this leak\'s creator.');
        setEvidenceLoading(false);
        return;
      }
      if (res.status === 404) {
        setEvidenceErrorKind('not_found');
        setEvidenceError('This leak no longer exists.');
        setEvidenceLoading(false);
        return;
      }
      if (!res.ok) {
        setEvidenceErrorKind('server');
        setEvidenceError('Could not load evidence. Refresh to retry.');
        setEvidenceLoading(false);
        return;
      }
      const data = await res.json();
      setEvidence(data);
      setEvidenceFetchedAt(Date.now());
      setEvidenceError('');
      setEvidenceErrorKind('');
      setEvidenceLoading(false);
    } catch {
      setEvidenceErrorKind('network');
      setEvidenceError('Network error loading evidence. Refresh to retry.');
      setEvidenceLoading(false);
    }
  }, [leakId]);

  // ── Initial evidence fetch. Re-fires when leakId changes (Next leak
  // navigates with replace, but if the leakId changes via param, refetch).
  useEffect(() => {
    setEvidenceLoading(true);
    setEvidence(null);
    setEvidenceError('');
    setFrameRefreshCount(0);
    fetchEvidence();
  }, [leakId, fetchEvidence]);

  // ── Pre-fill target creator from top_matches[0] when evidence lands.
  // Only sets if user hasn't already picked something — preserves manual
  // override on a re-fetch.
  useEffect(() => {
    if (!evidence) return;
    if (targetCreatorId) return;
    const top0 = evidence.top_matches && evidence.top_matches[0];
    if (top0 && top0.best_creator_id) {
      setTargetCreatorId(top0.best_creator_id);
    }
  }, [evidence, targetCreatorId]);

  // ── Dirty tracking (beforeUnload + Cancel guard).
  const isDirty = useMemo(() => {
    if (submitted) return false;
    return Boolean(
      action || hostObservation || freshness || evidenceUrlsText ||
      confidence || notes
    );
  }, [submitted, action, hostObservation, freshness, evidenceUrlsText,
      confidence, notes]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // ── Build the verification_intelligence JSONB sent to the RPC. Only
  // touched fields enter the JSON. Visual evidence from the gate is
  // folded in so the audit row carries it alongside the operator's
  // notes (matches the shape we used on the first re-attribution test).
  function buildIntelligence() {
    const obj = {};
    if (hostObservation.trim()) obj.host_routing_observation = hostObservation.trim();
    if (freshness)              obj.freshness_signal         = freshness;
    if (confidence)             obj.analyst_confidence       = confidence;
    const urls = evidenceUrlsText.split(/\n+/).map(s => s.trim()).filter(Boolean);
    if (urls.length) obj.evidence_urls = urls;
    const top0 = evidence && evidence.top_matches && evidence.top_matches[0];
    if (top0) {
      obj.visual_evidence = {
        hamming:        top0.best_hamming_top32,
        bimodal_gap:    top0.bimodal_gap,
        best_master_id: top0.best_master_id,
        best_handle:    top0.best_creator_handle,
      };
    }
    obj.operator_action = action;
    if (action === 'reattribute') {
      const target = evidence && evidence.top_matches &&
        evidence.top_matches.find(t => t.best_creator_id === targetCreatorId);
      if (target) obj.target_handle = target.best_creator_handle;
    }
    return obj;
  }

  // ── Refresh evidence if its signed URLs are nearing or past TTL.
  async function maybeRefreshIfStale() {
    if (!evidenceFetchedAt) return;
    const ageMs   = Date.now() - evidenceFetchedAt;
    const limitMs = (SIGNED_URL_TTL_SEC - REFRESH_BEFORE_EXPIRY_SEC) * 1000;
    if (ageMs > limitMs) await fetchEvidence();
  }

  // ── <img> onError handler. Refetch once or twice, then give up so we
  // don't loop on a permanently missing frame.
  async function handleFrameError() {
    if (frameRefreshing || evidenceLoading || submitting) return;
    if (frameRefreshCount >= MAX_FRAME_REFRESH_RETRIES) return;
    setFrameRefreshing(true);
    setFrameRefreshCount(c => c + 1);
    try {
      await fetchEvidence();
    } finally {
      setFrameRefreshing(false);
    }
  }

  // ── Submit.
  async function handleSubmit(e) {
    if (e) e.preventDefault();
    if (!action) return;
    const meta = ACTION_META[action];
    if (meta.requiresTarget && !targetCreatorId) return;

    setSubmitting(true);
    setSubmitError('');
    setSubmitErrorKind('');

    try {
      await maybeRefreshIfStale();

      const body = {
        leak_id:                   leakId,
        verdict:                   meta.verdict,
        routing_recommendation:    meta.routing,
        verification_intelligence: buildIntelligence(),
        notes:                     notes || undefined,
      };
      if (meta.requiresTarget) {
        body.corrected_creator_id = targetCreatorId;
      }

      const res = await fetch(`${API}/api/intelligence/verify`, {
        method:  'POST',
        headers: bearerHeaders(),
        body:    JSON.stringify(body),
      });

      if (res.status === 200) {
        const data = await res.json();
        setSubmittedPath((data && data.path) || 'written');
        setSubmitted(true);
        setSubmitting(false);
        return;
      }

      // Non-200 — preserve form, never destructive.
      let serverError = '';
      try { const j = await res.json(); serverError = (j && j.error) || ''; } catch {}
      if (res.status === 401) {
        setSubmitErrorKind('session_expired');
        setSubmitError(
          'Your session expired during submit. Sign in again in a new ' +
          'browser tab, then come back here and click Retry. Your form ' +
          'data is preserved on this page.'
        );
      } else if (res.status === 403) {
        setSubmitErrorKind('forbidden');
        setSubmitError(serverError || 'You are not authorized for this leak.');
      } else if (res.status === 404) {
        setSubmitErrorKind('not_found');
        setSubmitError(serverError || 'This leak no longer exists. Return to queue.');
      } else if (res.status >= 400 && res.status < 500) {
        setSubmitErrorKind('validation');
        setSubmitError(serverError || 'The server rejected the submission. Check your entries.');
      } else {
        setSubmitErrorKind('server');
        setSubmitError(serverError || 'The server failed to process the submission. Try again.');
      }
    } catch {
      setSubmitErrorKind('network');
      setSubmitError('Could not reach the server. Check your connection and try Retry.');
    }
    setSubmitting(false);
  }

  // ── Next leak — uses the role-appropriate queue endpoint.
  async function handleNext() {
    setNextLoading(true);
    try {
      const path = endpointForRole(staff && staff.role);
      const res = await fetch(`${API}/api/intelligence/${path}?limit=1&offset=0`,
        { headers: bearerHeaders() });
      if (res.status === 401) { evictToLogin(); return; }
      if (!res.ok) { setNextLoading(false); return; }
      const data = await res.json();
      const next = data && data.rows && data.rows[0];
      if (next) {
        // Attribution rows use leak_id; specialist rows use id. Either
        // works — useParams will pick up the new path segment.
        const nextId = next.leak_id || next.id;
        navigate(`/intelligence/review/${nextId}`, { replace: true });
        // Reset the form for the new leak; evidence will refetch via the
        // leakId-deps useEffect above.
        setAction(''); setTargetCreatorId('');
        setHostObservation(''); setFreshness('');
        setEvidenceUrlsText(''); setConfidence(''); setNotes('');
        setSubmitted(false); setSubmitError(''); setSubmitErrorKind('');
        setSubmittedPath('');
        setFrameRefreshCount(0);
      } else {
        // Empty queue — return to workspace.
        navigate('/intelligence', { replace: true });
      }
    } catch {
      setNextLoading(false);
    }
  }

  function handleCancel() {
    if (isDirty) {
      const ok = window.confirm('Discard your in-progress verdict and return to queue?');
      if (!ok) return;
    }
    navigate('/intelligence');
  }

  // ─── Render branches ──────────────────────────────────────────────

  // /me hasn't landed yet.
  if (!staff && !meError) {
    return (
      <PageShell leakId={leakId}>
        <div style={muted}>Verifying session…</div>
      </PageShell>
    );
  }
  if (meError) {
    return (
      <PageShell leakId={leakId}>
        <div style={errorBox}>{meError}</div>
      </PageShell>
    );
  }

  // Evidence error — fail-closed paths (403 / 404 / network / server).
  if (evidenceError) {
    return (
      <PageShell leakId={leakId}>
        <div style={errorBox}>
          <div style={{ marginBottom: 12 }}>{evidenceError}</div>
          <Link to="/intelligence" style={secondaryButton}>Back to queue</Link>
        </div>
      </PageShell>
    );
  }

  // Evidence loading.
  if (evidenceLoading || !evidence) {
    return (
      <PageShell leakId={leakId}>
        <div style={muted}>Loading evidence…</div>
      </PageShell>
    );
  }

  // Success state.
  if (submitted) {
    const isReattrib = action === 'reattribute';
    const staged     = submittedPath === 'staged';
    const headline = staged
      ? 'Submitted for review.'
      : isReattrib ? 'Re-attributed.' : 'Verdict recorded.';
    const sublineParts = [ACTION_META[action]?.verdict, ACTION_META[action]?.routing]
      .filter(Boolean);
    return (
      <PageShell leakId={leakId} failedGate={evidence.leak.failed_gate}>
        <div style={successBox}>
          <div style={{ fontSize: 16, color: '#86efac', marginBottom: 6 }}>
            {headline}
          </div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 18 }}>
            {sublineParts.join(' → ')}
            {staged && ' · operator approval required'}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleNext} disabled={nextLoading} style={primaryButton}>
              {nextLoading ? 'Loading…' : 'Next leak'}
            </button>
            <Link to="/intelligence" style={secondaryButton}>Back to queue</Link>
          </div>
        </div>
      </PageShell>
    );
  }

  // ─── Main form ───────────────────────────────────────────────────
  const leak        = evidence.leak;
  const topMatches  = evidence.top_matches || [];
  const top0        = topMatches[0] || null;
  const failedGate  = leak.failed_gate;
  const failedLabel = FAILED_GATE_LABEL[failedGate] || failedGate;

  const meta        = action ? ACTION_META[action] : null;
  const canSubmit   = Boolean(meta) && !submitting &&
                      (!meta.requiresTarget || Boolean(targetCreatorId));

  return (
    <PageShell leakId={leakId} failedGate={failedGate}>
      {/* Plain-language conflict sentence — the headline. */}
      {top0 && top0.best_creator_handle && leak.current_creator_stage && (
        <div style={conflictBlock}>
          <div style={{ fontSize: 13, color: '#fbbf24', marginBottom: 2,
                         letterSpacing: 0.6, textTransform: 'uppercase' }}>
            Gate refused — {failedLabel}
          </div>
          <div style={{ fontSize: 15, color: '#fff', lineHeight: 1.55 }}>
            The filename implies{' '}
            <strong style={{ color: '#94a3b8' }}>
              {leak.current_creator_stage}
            </strong>
            {' '}but the frame match points to{' '}
            <strong style={{ color: '#a78bfa' }}>
              {top0.best_creator_handle}
            </strong>
            {' '}(hamming {top0.best_hamming_top32 ?? '—'}, gap {top0.bimodal_gap ?? '—'}).
          </div>
        </div>
      )}

      {/* Side-by-side evidence: leak link (left) | primary master frame (right). */}
      <div style={evidenceGrid}>
        <div style={evidenceCell}>
          <div style={evidenceCellLabel}>The leak</div>
          <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>
            {leak.source_host || '(no host)'}
          </div>
          <a href={leak.leaked_url} target="_blank" rel="noreferrer noopener"
             style={{ color: '#a78bfa', fontSize: 13, wordBreak: 'break-all',
                      textDecoration: 'none' }}>
            {leak.leaked_url}
          </a>
          <div style={{ marginTop: 14, fontSize: 12, color: '#94a3b8' }}>
            Filed under{' '}
            <strong style={{ color: '#fff' }}>
              {leak.current_creator_stage || '(unknown)'}
            </strong>
          </div>
          <div style={{ marginTop: 4, fontSize: 11, color: '#666' }}>
            Detected {relativeTime(leak.detected_at)}
          </div>
        </div>

        <div style={evidenceCell}>
          <div style={evidenceCellLabel}>Primary visual match</div>
          {top0 && top0.signed_url ? (
            <img src={top0.signed_url}
                 alt={`${top0.best_creator_handle || 'master'} reference frame`}
                 onError={handleFrameError}
                 style={frameImg} />
          ) : (
            <div style={frameUnavailable}>
              Reference frame unavailable
              {frameRefreshCount >= MAX_FRAME_REFRESH_RETRIES &&
                ' (retries exhausted)'}
            </div>
          )}
          {top0 && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#94a3b8' }}>
              <strong style={{ color: '#a78bfa' }}>
                {top0.best_creator_handle || '(unknown)'}
              </strong>
              {' '}master · {top0.best_source_frame_name || '—'}
            </div>
          )}
          {top0 && (
            <div style={{ marginTop: 2, fontSize: 11, color: '#666' }}>
              hamming {top0.best_hamming_top32 ?? '—'} · gap {top0.bimodal_gap ?? '—'}
              {top0.role ? ` · ${top0.role}` : ''}
            </div>
          )}
        </div>
      </div>

      {/* Competing candidates — every top_matches entry visible so the
          operator can scan ALL of the gate's hits before picking a
          re-attribution target. Primary is also rendered here for
          consistency; selected highlights. */}
      {topMatches.length > 1 && (
        <Section title={`Candidate matches (${topMatches.length})`}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {topMatches.map((tm, i) => (
              <CandidateChip
                key={`${tm.best_master_id || 'unk'}_${tm.best_source_frame_name || i}`}
                tm={tm}
                isPrimary={i === 0}
                isSelected={action === 'reattribute'
                            && tm.best_creator_id === targetCreatorId}
                showPickButton={action === 'reattribute'}
                onPick={() => setTargetCreatorId(tm.best_creator_id)}
              />
            ))}
          </div>
        </Section>
      )}

      {/* Action buttons. */}
      <Section title="Action (required)">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {top0 && top0.best_creator_handle && (
            <ActionButton
              actionKey="reattribute"
              active={action === 'reattribute'}
              label={`Re-attribute to ${top0.best_creator_handle}`}
              onClick={() => setAction('reattribute')}
            />
          )}
          <ActionButton
            actionKey="confirm"
            active={action === 'confirm'}
            label="Confirm current creator"
            onClick={() => setAction('confirm')}
          />
          <ActionButton
            actionKey="false_positive"
            active={action === 'false_positive'}
            label="Not a leak / false match"
            onClick={() => setAction('false_positive')}
          />
          <ActionButton
            actionKey="insufficient"
            active={action === 'insufficient'}
            label="Can't tell"
            onClick={() => setAction('insufficient')}
          />
        </div>
        {action === 'reattribute' && (
          <div style={hintText}>
            Target creator pre-filled from the primary visual match. Pick a
            different candidate above to re-attribute to that creator instead.
          </div>
        )}
        {action === 'insufficient' && (
          <div style={hintText}>
            No DMCA filing path on insufficient-evidence verdicts — filing
            requires good-faith assertion under § 512(c)(3).
          </div>
        )}
      </Section>

      {/* Verification intelligence (optional, kept from Slice 1 card). */}
      <Section title="Verification intelligence (optional)">
        <Field label="Host routing observation">
          <input type="text" value={hostObservation}
                 onChange={e => setHostObservation(e.target.value)}
                 placeholder="e.g. host returned 403, try CDN dmca"
                 style={inputStyle} />
        </Field>
        <Field label="Freshness signal">
          <RadioRow value={freshness} onChange={setFreshness} options={FRESHNESS_OPTIONS} />
        </Field>
        <Field label="Evidence URLs (one per line)">
          <textarea value={evidenceUrlsText}
                    onChange={e => setEvidenceUrlsText(e.target.value)}
                    placeholder="https://archive.org/web/…&#10;https://imgur.com/…"
                    rows={3}
                    style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12 }} />
        </Field>
        <Field label="Analyst confidence">
          <RadioRow value={confidence} onChange={setConfidence} options={CONFIDENCE_OPTIONS} />
        </Field>
      </Section>

      <Section title={`Notes (optional, ${notes.length}/${NOTES_MAX})`}>
        <textarea value={notes}
                  onChange={e => setNotes(e.target.value.slice(0, NOTES_MAX))}
                  rows={4} style={inputStyle} />
      </Section>

      {/* Error surfacing — preserves form, never destructive. */}
      {submitError && (
        <div style={{
          ...errorBox,
          borderColor: submitErrorKind === 'session_expired' ? '#f59e0b' : 'rgba(248,113,113,0.3)',
          background:  submitErrorKind === 'session_expired' ? 'rgba(245,158,11,0.08)' : 'rgba(248,113,113,0.08)',
          color:       submitErrorKind === 'session_expired' ? '#fbbf24' : '#f87171',
          marginTop:   18,
          whiteSpace:  'pre-wrap',
        }}>
          {submitError}
          <div style={{ marginTop: 12 }}>
            <button type="button" onClick={handleSubmit}
                    disabled={submitting}
                    style={secondaryButton}>
              {submitting ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        </div>
      )}

      {/* Submit + Cancel. */}
      <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
        <button type="button" onClick={handleSubmit} disabled={!canSubmit}
                style={canSubmit ? primaryButton : primaryButtonDisabled}>
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
        <button type="button" onClick={handleCancel} style={secondaryButton}>
          Cancel
        </button>
      </div>
    </PageShell>
  );
}

// ─── Subcomponents ────────────────────────────────────────────────

function PageShell({ leakId, failedGate, children }) {
  return (
    <div style={pageStyle}>
      <TopBar leakId={leakId} failedGate={failedGate} />
      <main style={mainStyle}>{children}</main>
    </div>
  );
}

function TopBar({ leakId, failedGate }) {
  const failedLabel = FAILED_GATE_LABEL[failedGate] || failedGate;
  return (
    <header style={headerStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Link to="/intelligence" style={{
          color: '#94a3b8', fontSize: 13, textDecoration: 'none',
        }}>
          ← Back to queue
        </Link>
        <div style={{ fontSize: 11, color: '#666', letterSpacing: 1, textTransform: 'uppercase' }}>
          Review · {leakId && leakId.slice(0, 8)}…
        </div>
      </div>
      {failedGate && (
        <span style={topbarFailedGateBadge}>{failedLabel}</span>
      )}
    </header>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase',
                     letterSpacing: 1.4, marginBottom: 10 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

function RadioRow({ value, onChange, options }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {options.map(o => (
        <button type="button" key={o}
                onClick={() => onChange(value === o ? '' : o)}
                style={value === o ? radioSelected : radio}>
          {o}
        </button>
      ))}
    </div>
  );
}

function ActionButton({ actionKey, active, label, onClick }) {
  const meta = ACTION_META[actionKey];
  const styleActive = {
    ...actionButton,
    background:  meta.bg,
    borderColor: meta.border,
    color:       meta.color,
  };
  return (
    <button type="button" onClick={onClick}
            style={active ? styleActive : actionButton}>
      {label}
    </button>
  );
}

function CandidateChip({ tm, isPrimary, isSelected, showPickButton, onPick }) {
  return (
    <div style={isSelected ? candidateChipSelected : candidateChip}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        {tm.signed_url ? (
          <img src={tm.signed_url}
               alt={`${tm.best_creator_handle || 'master'} thumb`}
               style={candidateThumb} />
        ) : (
          <div style={candidateThumbPlaceholder}>n/a</div>
        )}
        <div style={{ flex: 1, minWidth: 130 }}>
          <div style={{ fontSize: 12, color: '#a78bfa', fontWeight: 600 }}>
            {tm.best_creator_handle || '(unknown)'}
            {isPrimary && (
              <span style={{ marginLeft: 6, fontSize: 10, color: '#666',
                             fontWeight: 400, textTransform: 'uppercase',
                             letterSpacing: 0.6 }}>
                primary
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
            h={tm.best_hamming_top32 ?? '—'} · gap={tm.bimodal_gap ?? '—'}
          </div>
          <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>
            {tm.best_source_frame_name || '—'}{tm.role ? ` · ${tm.role}` : ''}
          </div>
          {showPickButton && (
            isSelected ? (
              <div style={{ marginTop: 6, fontSize: 11, color: '#86efac' }}>
                ✓ Selected target
              </div>
            ) : (
              <button type="button" onClick={onPick}
                      style={candidatePickButton}>
                Use as target
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────
const pageStyle = {
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #0f0f1e 0%, #0a0a0f 100%)',
  color: '#fff',
};
const headerStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '16px 32px', borderBottom: '1px solid #222',
  background: 'rgba(15,15,30,0.6)',
};
const mainStyle = {
  maxWidth: 960, margin: '0 auto', padding: '28px 24px',
};
const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  background: '#0d0d18', border: '1px solid #2a2a2a', borderRadius: 6,
  color: '#fff', fontSize: 13, padding: '8px 10px',
};
const hintText = {
  fontSize: 12, color: '#666', marginTop: 10, lineHeight: 1.5,
};

const conflictBlock = {
  background: 'rgba(245,158,11,0.06)',
  border: '1px solid rgba(245,158,11,0.25)',
  borderRadius: 10,
  padding: '14px 18px',
  marginBottom: 16,
};

const evidenceGrid = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 14,
  marginBottom: 14,
};
const evidenceCell = {
  background: '#111',
  border: '1px solid #222',
  borderRadius: 10,
  padding: '16px 18px',
};
const evidenceCellLabel = {
  fontSize: 10, color: '#666', textTransform: 'uppercase',
  letterSpacing: 1.4, marginBottom: 8,
};
const frameImg = {
  display: 'block',
  width: '100%',
  maxHeight: 280,
  objectFit: 'contain',
  background: '#0a0a0f',
  borderRadius: 6,
  border: '1px solid #1a1a1a',
};
const frameUnavailable = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  height: 180, fontSize: 12, color: '#666',
  background: '#0a0a0f', borderRadius: 6, border: '1px dashed #2a2a2a',
};

const topbarFailedGateBadge = {
  display: 'inline-block',
  padding: '3px 10px',
  borderRadius: 4,
  background: 'rgba(245,158,11,0.10)',
  color: '#f59e0b',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.4,
};

const actionButton = {
  padding: '12px 14px', borderRadius: 6,
  background: '#1a1a1a', border: '1px solid #2a2a2a',
  color: '#94a3b8', fontSize: 13, cursor: 'pointer',
  textAlign: 'left',
};

const radio = {
  padding: '6px 12px', borderRadius: 4,
  background: '#1a1a1a', border: '1px solid #2a2a2a',
  color: '#94a3b8', fontSize: 12, cursor: 'pointer',
};
const radioSelected = {
  ...radio,
  background: 'rgba(124,58,237,0.18)', borderColor: '#7c3aed', color: '#e0d4fb',
};

const candidateChip = {
  background: '#0d0d18',
  border: '1px solid #1a1a1a',
  borderRadius: 8,
  padding: '10px 12px',
  minWidth: 220,
};
const candidateChipSelected = {
  ...candidateChip,
  border: '1px solid #7c3aed',
  background: 'rgba(124,58,237,0.08)',
};
const candidateThumb = {
  width: 64, height: 64, objectFit: 'cover',
  borderRadius: 4, background: '#0a0a0f',
  border: '1px solid #1a1a1a',
};
const candidateThumbPlaceholder = {
  ...candidateThumb,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 10, color: '#444',
};
const candidatePickButton = {
  marginTop: 6, padding: '4px 10px', borderRadius: 4,
  background: '#1a1a1a', border: '1px solid #2a2a2a',
  color: '#94a3b8', fontSize: 11, cursor: 'pointer',
};

const primaryButton = {
  padding: '10px 20px', borderRadius: 6,
  background: '#7c3aed', color: '#fff', border: 'none',
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
  textDecoration: 'none', display: 'inline-block',
};
const primaryButtonDisabled = {
  ...primaryButton, background: '#1a1a1a', color: '#444',
  border: '1px solid #2a2a2a', cursor: 'not-allowed',
};
const secondaryButton = {
  padding: '10px 18px', borderRadius: 6,
  background: '#1a1a1a', border: '1px solid #2a2a2a',
  color: '#94a3b8', fontSize: 13, cursor: 'pointer',
  textDecoration: 'none', display: 'inline-block',
};
const muted = {
  color: '#666', fontSize: 14, textAlign: 'center', padding: '40px 0',
};
const errorBox = {
  padding: '14px 18px',
  background: 'rgba(248,113,113,0.08)',
  border: '1px solid rgba(248,113,113,0.3)',
  borderRadius: 8, color: '#f87171', fontSize: 13,
};
const successBox = {
  padding: '36px 24px',
  background: 'rgba(34,197,94,0.06)',
  border: '1px solid rgba(34,197,94,0.25)',
  borderRadius: 12,
};

